import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { videosForRoute } from "@/data/pageVideos";
import { videoObject } from "@/lib/schema";

/**
 * Emits VideoObject markup for whichever route is showing, if that page embeds
 * YouTube videos. Mounted once inside the router so every route is covered
 * without touching individual centre pages.
 *
 * Titles are the real YouTube titles (fetched via oEmbed at build time by
 * scripts/import-page-videos.cjs). uploadDate and duration need the YouTube
 * Data API and are therefore absent — Google treats them as required for video
 * rich results, so this markup describes the videos correctly but will not by
 * itself qualify for a video rich result until those two fields are supplied.
 */
export default function RouteVideoSchema() {
  const { pathname } = useLocation();

  useEffect(() => {
    const videos = videosForRoute(pathname);
    if (!videos.length) return;

    // One <script> per video, so each VideoObject is listed as its own item in
    // validators rather than collapsed into a single array entry.
    const els = videos.map((v) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-mv-video", "1");
      el.textContent = JSON.stringify(
        videoObject({
          name: v.title,
          // Factual: the video's own title and the channel that published it.
          description: v.author
            ? `${v.title} — video published on YouTube by ${v.author}.`
            : `${v.title} — video published on YouTube.`,
          youtubeId: v.id,
          publisherName: v.author,
        })
      );
      document.head.appendChild(el);
      return el;
    });
    return () => {
      els.forEach((el) => el.remove());
    };
  }, [pathname]);

  return null;
}
